const find = require('lodash/find');
const isEmpty = require('lodash/isEmpty');
const get = require('lodash/get');

const logger = require('../../logger');

const getAvailableProducts = products =>
  (products || []).filter(product => product.variants && product.variants.length > 0);

const getAvailableProductIds = products =>
  getAvailableProducts(products).map(product => product.id);

const getAvailableProductLanguages = product => {
  if (!product) {
    return [];
  }

  return product.variants
    .map(variant => variant.options.locale)
    .filter((value, index, array) => array.indexOf(value) === index);
};

const findProductById = (products, productId) => {
  return find(products, product => product.id === productId) || null;
};

/**
 * finds all matching variants based on the filter passed.
 * @param {Object[]} variants a list of product variants from Eagle
 * @param {Object} filter can include any of the keys in variants[].options, and the variants[].formatId field as well.
 * @param {number} [limit] only return a limited amount of filtered variants, Default: no limit
 * @returns {Array} filtered list of product variants from Eagle
 */
const findProductVariants = (variants, filter, limit) => {
  if (!variants || isEmpty(variants) || !filter || isEmpty(filter)) {
    return [];
  }
  // Default limit to length of variants if argument was undefined
  limit = limit || variants.length;

  // Could come from product customisations where it is snake_case OR from eagle where it is camelCase
  const formatId = filter.format_identifier || filter.formatId;
  const filterKeys = Object.keys(filter);

  const filteredVariants = [];
  let currentCount = 0;

  for (let i = 0; i < variants.length; i++) {
    // If the limit that the caller has set has been reached then break out of loop
    if (currentCount >= limit) {
      break;
    }

    const variant = variants[i];
    // If the format that the caller is looking for is not matching the format of the variant, then
    // there is no point in going through the customisations, we can straight
    // away ignore this variant and skip to the next one.
    if (formatId && formatId !== variant.formatId) {
      continue;
    }

    const isValid = filterKeys.every(filterKey => {
      if (!variant.options[filterKey]) {
        return true;
      }
      return variant.options[filterKey] === filter[filterKey];
    });

    if (isValid) {
      filteredVariants.push(variant);
      currentCount++;
    }
  }

  return filteredVariants;
};

const findProductVariantsByLocale = (variants, locale) => {
  return findProductVariants(variants, { locale });
};

const findProductVariant = (variants, options) => {
  return findProductVariants(variants, options, 1)[0] || {};
};

const findLowestVariantPrice = (variants, { locale } = {}) => {
  if (!variants) {
    return {};
  }

  if (locale) {
    const localeVariants = findProductVariantsByLocale(variants, locale);
    variants = isEmpty(localeVariants) ? variants : localeVariants;
  }

  const variant = variants.reduce(
    (prev, curr) => {
      const currIsCheaper = prev.price.amount > curr.price.amount;
      const prevIsCheaper = prev.price.amount < curr.price.amount;
      if (!currIsCheaper && !prevIsCheaper) {
        return curr.savings ? curr : prev;
      }
      return prevIsCheaper ? prev : curr;
    },
  );

  let pricing = { price: variant.price };
  if (variant.savings) {
    pricing.savings = variant.savings;
  }

  return pricing;
};

const findLowestProductPrice = (products, productId, options) => {
  const availableProducts = getAvailableProducts(products);
  const product = findProductById(availableProducts, productId);
  return product && findLowestVariantPrice(product.variants, options);
};

const findVariantPrice = (products, productId, options) => {
  const product = findProductById(products, productId);
  if (!product) {
    logger.error({
      msg: `failed to find ${productId}`,
      productId,
      products
    });
    return '';
  }
  const variant = findProductVariant(product.variants, options);
  const lowestPricing = findLowestVariantPrice(product.variants);
  return get(variant, 'price.formatted', null) || get(lowestPricing, 'price.formatted', '');
};

module.exports = {
  getAvailableProducts,
  getAvailableProductIds,
  findProductById,
  findLowestVariantPrice,
  findLowestProductPrice,
  findVariantPrice,
  findProductVariants,
  getAvailableProductLanguages,
  findProductVariantsByLocale,
  findProductVariant,
};
